/**
 * Labstep
 *
 * @module components/PostFilter/Menu
 * @desc Filters menu
 */

import { ExperimentWorkflowFilterResourceSelectContainer as ExperimentWorkflowFilterResource } from 'labstep-web/components/ExperimentWorkflow/Filter/Resource/Select';
import { FilterAssignedTo } from 'labstep-web/components/Filter/AssignedTo';
import { FilterAvailableResourceItemCount } from 'labstep-web/components/Filter/AvailableResourceItemCount';
import { FilterCreatedBy } from 'labstep-web/components/Filter/CreatedBy';
import { FilterDateRange } from 'labstep-web/components/Filter/DateRange';
import { FilterDeleted } from 'labstep-web/components/Filter/Deleted';
import { FilterMenuList } from 'labstep-web/components/Filter/Menu/List';
import { FilterMetadata } from 'labstep-web/components/Filter/Metadata';
import { FilterResourceItemStatus } from 'labstep-web/components/Filter/ResourceItemStatus';
import { FilterTagSelectReadContainer as FilterTagSelect } from 'labstep-web/components/Filter/Tag/Select';
import { FilterPostNewIndexDevice } from 'labstep-web/components/FilterPost/NewIndex/Device';
import { FilterPostNewIndexMetadataExperimentWorkflow } from 'labstep-web/components/FilterPost/NewIndex/MetadataExperimentWorkflow';
import { FilterPostNewIndexProtocol } from 'labstep-web/components/FilterPost/NewIndex/Protocol';
import { FilterPostNewIndexResource } from 'labstep-web/components/FilterPost/NewIndex/Resource';
import { FilterPostNewIndexTag } from 'labstep-web/components/FilterPost/NewIndex/Tag';
import { MoleculeFilter } from 'labstep-web/components/Molecule/Filter';
import ResourceItemFilterResourceLocation from 'labstep-web/components/ResourceItem/Filter/ResourceLocation';
import { Filter } from 'labstep-web/constants/filters';
import { ICONS } from 'labstep-web/constants/icons';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import type React from 'react';
import type { FilterPostMenuProps } from './types';

export const FilterPostMenu: React.FC<FilterPostMenuProps> = ({
  filters,
  entityName,
  setParams,
  searchParams,
  addPostFilter,
}) => {
  const filterMenuContent = [
    {
      name: Filter.deleted,
      component: (
        <FilterDeleted
          setParams={setParams}
          searchParams={searchParams}
          addPostFilter={addPostFilter}
        />
      ),
    },
    {
      name: Filter.metadata,
      component: (
        <FilterMetadata
          entityName={entityName || ''}
          addPostFilter={addPostFilter}
        />
      ),
    },
    {
      name: Filter.resource_metadata,
      component: (
        <FilterMetadata
          entityName={ResourceItem.entityName}
          childEntityName={Resource.entityName}
          addPostFilter={addPostFilter}
        />
      ),
    },
    {
      name: Filter.metadata_experiment_workflow,
      component: (
        <FilterPostNewIndexMetadataExperimentWorkflow
          addPostFilter={addPostFilter}
        />
      ),
    },
    {
      name: Filter.resource_item_status,
      component: (
        <FilterResourceItemStatus
          isOrderRequest={false}
          setParams={setParams}
          searchParams={searchParams}
          addPostFilter={addPostFilter}
        />
      ),
    },
    {
      name: Filter.created_by,
      component: (
        <FilterCreatedBy
          searchParams={searchParams}
          setParams={setParams}
          filter={Filter.created_by}
          addPostFilter={addPostFilter}
        />
      ),
    },
    {
      name: Filter.date_range,
      component: (
        <FilterDateRange
          setParams={setParams}
          searchParams={searchParams}
          addPostFilter={addPostFilter}
        />
      ),
    },
    {
      name: Filter.assigned_to,
      component: (
        <FilterAssignedTo
          searchParams={searchParams}
          setParams={setParams}
          addPostFilter={addPostFilter}
        />
      ),
    },
    {
      name: Filter.resource_location,
      component: (
        <ResourceItemFilterResourceLocation
          setParams={setParams}
          searchParams={searchParams}
          addPostFilter={addPostFilter}
        />
      ),
    },
    {
      name: Filter.available_resource_item_count,
      component: (
        <FilterAvailableResourceItemCount
          setParams={setParams}
          searchParams={searchParams}
          addPostFilter={addPostFilter}
        />
      ),
    },
    {
      name: Filter.protocol,
      component: (
        <FilterPostNewIndexProtocol addPostFilter={addPostFilter} />
      ),
    },
    {
      name: Filter.device,
      component: (
        <FilterPostNewIndexDevice addPostFilter={addPostFilter} />
      ),
    },
    {
      name: Filter.resource,
      component: (
        <FilterPostNewIndexResource addPostFilter={addPostFilter} />
      ),
      actionComponentProps: {
        text: 'Resource used',
        icon: ICONS.resource.primary,
      },
    },
    {
      name: Filter.molecule,
      component: (
        <MoleculeFilter
          entityName={entityName}
          setParams={setParams}
          searchParams={searchParams}
          addPostFilter={addPostFilter}
        />
      ),
      actionComponentProps: {
        text: 'Chemical Structure',
        icon: ICONS.molecule.primary,
      },
    },
    {
      name: Filter.tag,
      component: (
        <FilterPostNewIndexTag addPostFilter={addPostFilter} />
      ),
    },
  ];
  return (
    <FilterMenuList
      filters={filters}
      filtersContent={filterMenuContent}
    />
  );
};
